import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { videoPosterAttribute } from '../../utils/albumVideoThumbnail';

export interface VideoWithLoadingOverlayProps {
   src: string;
   poster?: string;
   controls?: boolean;
   playsInline?: boolean;
   /** When true, wrapper fills parent (lightbox). When false, video sizes naturally (e.g. maxHeight 80vh). */
   fillContainer?: boolean;
   style?: React.CSSProperties;
}

/**
 * Video with loading / buffering feedback. Uses preload="metadata" and no autoplay.
 */
export function VideoWithLoadingOverlay({
   src,
   poster,
   controls = true,
   playsInline = true,
   fillContainer = true,
   style,
}: VideoWithLoadingOverlayProps) {
   const [loading, setLoading] = useState(true);
   const [buffering, setBuffering] = useState(false);
   const safePoster = videoPosterAttribute(poster, src);

   useEffect(() => {
      setLoading(true);
      setBuffering(false);
   }, [src]);

   return (
      <Box
         sx={{
            position: 'relative',
            width: '100%',
            ...(fillContainer
               ? {
                    height: '100%',
                    minHeight: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                 }
               : {
                    display: 'block',
                    maxWidth: '100%',
                 }),
         }}
      >
         {loading && (
            <Box
               sx={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1.5,
                  bgcolor: 'rgba(0,0,0,0.55)',
                  zIndex: 2,
                  borderRadius: fillContainer ? 0 : 1,
                  minHeight: fillContainer ? undefined : 160,
               }}
            >
               <CircularProgress sx={{ color: '#00C4A7' }} />
               <Typography variant="body2" color="white">
                  Loading video…
               </Typography>
            </Box>
         )}
         {buffering && !loading && (
            <Box
               sx={{
                  position: 'absolute',
                  bottom: fillContainer ? 24 : 12,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  zIndex: 3,
                  bgcolor: 'rgba(0,0,0,0.65)',
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 1,
               }}
            >
               <Typography variant="caption" color="white">
                  Buffering…
               </Typography>
            </Box>
         )}
         <video
            key={src}
            src={src}
            poster={safePoster}
            controls={controls}
            playsInline={playsInline}
            preload="metadata"
            onLoadStart={() => setLoading(true)}
            /** With preload=metadata, canplay may wait until user presses play — hide spinner once metadata/poster is usable */
            onLoadedMetadata={() => setLoading(false)}
            onCanPlay={() => setLoading(false)}
            onPlaying={() => {
               setLoading(false);
               setBuffering(false);
            }}
            onWaiting={() => setBuffering(true)}
            onError={() => setLoading(false)}
            style={{
               display: 'block',
               maxWidth: '100%',
               ...(fillContainer
                  ? { width: '100%', height: '100%', maxHeight: '100%', objectFit: 'contain' as const }
                  : { width: '100%', height: 'auto', objectFit: 'contain' as const }),
               ...style,
            }}
         />
      </Box>
   );
}
